//Utils
import { translateAssetTypeName } from "../../utils";
//Tips
import { NavButtonitem } from "./types";
//Icons
import { BsToggle2Off, BsToggle2On } from "react-icons/bs";
import {
  MdNavigateBefore,
  MdNavigateNext,
  MdOutlineBusiness,
  MdOutlineMultilineChart,
} from "react-icons/md";
import { AiOutlineCloudDownload } from "react-icons/ai";
import { GiRocketFlight } from "react-icons/gi";
//Components
import { fullPageFatalError } from "../../components/AnalysisPageComponents/Cards/ErrorsCard";
import AssetDetailsHover from "../../components/AssetDetailsHover";
import ProgressBarScore from "../../components/ProgressBarPoints";
//Misc
import { useEffect, useState } from "react";
import { OverlayTrigger } from "react-bootstrap";
import { isMobile } from "react-device-detect";
import { useHistory, useParams } from "react-router-dom";
import { projectPrimaryColor } from "../../generalConfigs";
import loadingSkeleton from "./skeleton";
import useIcons from "../../hooks/useIcons";
import { useTour } from "@reactour/tour";
import useGeneralContext from "../../context/GeneralContext";
import useRanking from "../../hooks/useRanking";
import { RankingItem } from "../../@types/Ranking";
import GradientText from "../../components/Logo";
import { Dictionary } from "../../@types/shared";
import { eInvestorType } from "../../@types/Users";
import { eAssetType } from "../../@types/Asset";
import { IoSparklesOutline } from "react-icons/io5";
import { FaSortAmountDown } from "react-icons/fa";
import useWindowDimensions from "../../hooks/useWindowDimension";
import { RiStockLine } from "react-icons/ri";
import { HiPresentationChartLine } from "react-icons/hi";

export default function RankingPage() {
  const [actualPage, setActualPage] = useState<number>(1);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [rankingItems, setRankingItems] = useState<RankingItem[]>([]);
  const { assetType: assetTypeUrlParam } = useParams<{
    assetType: string;
  }>();
  const [selectedColumn, setSelectedColumn] = useState<string>(
    assetTypeUrlParam === "stock" ? "health_buy" : "ai_score"
  );
  const [isFatalError, setIsFatalError] = useState<boolean>(false);
  const [investorType, setInvestorType] = useState<eInvestorType | null>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [onlyBr, setOnlyBr] = useState<boolean>(assetTypeUrlParam === "stock");

  const { width } = useWindowDimensions();
  const { getURI } = useIcons();
  const history = useHistory();
  const { setSteps, setIsOpen, setCurrentStep } = useTour();
  const { tourStart, setDisablePrev } = useGeneralContext();
  const { getPaginator } = useRanking();

  const navButtons =
    assetTypeUrlParam === "stock" ? stockButtons : criptoButtons;
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setIsFatalError(false);
      window.scrollTo(0, 0);

      const assetTypeInt = getAssetTypeInt(assetTypeUrlParam);
      const paginator = await getPaginator(
        assetTypeInt,
        actualPage,
        itemsPerPage,
        selectedColumn,
        assetTypeInt === eAssetType.STOCK && onlyBr
      );

      if (paginator) {
        setRankingItems(paginator.data);
        setHasNextPage(paginator.next_page_url !== null);
        setTotalPages(paginator.last_page);

        if (paginator.data.length > 0) {
          setInvestorType(paginator.data[0].investor_type);
        }
      } else {
        setIsFatalError(true);
      }

      setIsLoading(false);
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetTypeUrlParam, selectedColumn, actualPage, onlyBr]);

  useEffect(() => {
    const newSelectedColumn =
      assetTypeUrlParam === "stock" ? "health_buy" : "ai_score";

    setSelectedColumn(newSelectedColumn);

    if (assetTypeUrlParam === "stock") {
      setOnlyBr(true);
    }
  }, [assetTypeUrlParam]);

  useEffect(() => {
    const steps = [
      {
        selector: "#ranking-step-1",
        content:
          "Caso você não saiba quais ativos ficar de olho, aqui na página de ranking você consegue encontrar os melhores negócios para compor sua carteira ou realizar operações de curto prazo.",
        action: () => {
          setDisablePrev(true);
        },
      },
      {
        selector: "#ranking-step-2",
        position: "bottom",
        content:
          "No ranking, você pode categorizar pelo tipo de análise que mais faz sentido para você, todo ranking é adaptado para seu perfil de investidor.",
        actionAfter: () => {
          if(setSteps) {
            setSteps([]);
          }
          setCurrentStep(0);
          history.push("/wallet");
        },
      },
    ];

    if (setSteps && tourStart && !isLoading) {
      setTimeout(() => {
        // @ts-ignore
        setSteps(steps);
        setIsOpen(true);
        setCurrentStep(0);
      }, 1500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, tourStart]);

  function getTagGradient(tags: string[]) {
    let output = "";

    tags.forEach((tag, i) => {
      output = output.concat("#" + tag + " ");
    });

    return output.trim();
  }

  if (isLoading) {
    return loadingSkeleton();
  } else if (isFatalError) {
    return fullPageFatalError();
  }

  function getSwitch() {
    if (assetTypeUrlParam !== "stock") {
      return <></>;
    }

    let icon;

    if (onlyBr) {
      icon = (
        <BsToggle2On
          onClick={() => setOnlyBr(!onlyBr)}
          cursor={"pointer"}
          color="var(--primary-blue)"
          style={{ marginTop: -6, marginBottom: 10 }}
          size={30}
        />
      );
    } else {
      icon = (
        <BsToggle2Off
          onClick={() => setOnlyBr(!onlyBr)}
          cursor={"pointer"}
          color="var(--secondary)"
          style={{ marginTop: -6, marginBottom: 10 }}
          size={30}
        />
      );
    }

    return (
      <>
        {icon}
        <p className="mg-l-10" onClick={() => setOnlyBr(!onlyBr)}>
          Apenas Brasil.{" "}
        </p>
        <img
          style={{ borderRadius: 15 }}
          className="mg-b-5 mg-l-5"
          height={20}
          width={20}
          src={getURI("flags/br.svg")}
        ></img>
      </>
    );
  }

  const barIcon = getBarIcon(assetTypeUrlParam);

  return (
    <div
      className={
        isMobile
          ? "az-content az-content-mail"
          : "az-content az-content-mail mg-t-20"
      }
    >
      <div className="container">
        <div className="content-wrapper w-100">
          <div className="row">
            <div className="col-lg-12">
              <div className="card mg-b-20" style={{ borderRadius: 20 }}>
                <div className="card-body">
                  <div className="d-sm-flex pb-4 mb-4 border-bottom">
                    <div
                      className="d-flex align-items-center"
                      id="ranking-step-1"
                    >
                      <div>
                        <h5 className="page-title mb-n2">
                          <div className="media-icon">
                            <i>{barIcon}</i>
                          </div>
                          <span className="mg-l-7">
                            Ranking de{" "}
                            {translateAssetTypeName(assetTypeUrlParam)}
                          </span>
                        </h5>
                      </div>
                    </div>
                  </div>
                  <div>
                    <p className="tx-14">
                      A cada análise realizada por nossa IA, um relatório é
                      criado. A partir desses relatórios, conseguimos criar um
                      Ranking com os ativos mais bem avaliados para cada
                      categoria, com um indicador inteligente que leva em
                      consideração diversos fatores. Você pode selecionar quais
                      fatores quer levar em consideração no ranking utilizando
                      um dos botões abaixo.
                    </p>
                  </div>
                  <hr className="mg-t-10" />
                  <div className="nav-scroller">
                    <ul
                      className="nav nav-tabs tickets-tab-switch"
                      id="ranking-step-2"
                    >
                      {navButtons.map((item, i) => {
                        const isActive = selectedColumn === item.column;

                        return (
                          <li
                            className="nav-item cursor-pointer"
                            key={i}
                            onClick={() => {
                              setSelectedColumn(item.column);
                              setActualPage(1);
                            }}
                          >
                            <div
                              className={
                                isActive ? "nav-link active" : "nav-link"
                              }
                              style={{ borderRadius: 20, marginBottom: 8 }}
                            >
                              <i className="pd-l-10 mg-b-2">{item.icon}</i>
                              <span className="pd-l-5">{item.title}</span>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>

                  <div className="tab-content border-0 tab-content-basic animated fadeInDown">
                    <div
                      className="tab-pane fade show active"
                      id="open-tickets"
                      role="tabpanel"
                      aria-labelledby="open-tickets"
                    >
                      <div className="row mg-t-20">
                        <div className="col-12 col-sm-6">
                          <div className="d-flex">
                            <FaSortAmountDown
                              color="var(--primary-blue)"
                              size={15}
                            />
                            <p className="mg-l-5">
                              Ordenar por: maior para menor.
                            </p>
                          </div>
                        </div>

                        <div className="col-12 col-sm-6">
                          <div
                            className={`d-flex justify-content-${width >= 576 ? "end" : "start"
                              }`}
                          >
                            {getSwitch()}
                          </div>
                        </div>
                      </div>

                      {rankingItems.map((item: any) => {
                        const assetData = item.asset_data;

                        return (
                          <div
                            className="tickets-card row"
                            onClick={() => {
                              history.push(`/analysis/${assetData.encoded_id}`);
                            }}
                          >
                            <div className="tickets-details col-lg-6">
                              <OverlayTrigger
                                delay={{ show: 100, hide: 100 }}
                                overlay={AssetDetailsHover({
                                  id: assetData.encoded_id,
                                  ...assetData,
                                })}
                              >
                                <div className="wrapper">
                                  <img
                                    className="img-xs rounded-circle"
                                    src={getURI(assetData.icon)}
                                    alt="asset profile picture"
                                  ></img>
                                  <h5 className="pd-l-10">{assetData.name}</h5>
                                </div>
                              </OverlayTrigger>
                              <div
                                className="d-flex tx-16 tx-medium mt-1"
                                style={{ gap: 10 }}
                              >
                                <GradientText
                                  text={getTagGradient(item.tags)}
                                />
                              </div>
                              Ticker:{" "}
                              <div
                                className="badge badge-pill badge-secondary"
                                style={{ marginLeft: 0 }}
                              >
                                {assetData.symbol}
                              </div>
                              <div>
                                <span className="tx-10">
                                  {`Última análise ás ${new Date(
                                    item.updated_at
                                  ).toLocaleTimeString("pt-BR")} de ${new Date(
                                    item.updated_at
                                  ).toLocaleDateString("pt-BR")}`}
                                </span>
                              </div>
                            </div>

                            <div className="ticket-float col-lg-2 col-sm-6 d-inline">
                              <div style={{ float: "right" }}>
                                {
                                  <ProgressBarScore
                                    actualPoints={item[selectedColumn]}
                                    maxPoints={getMaxPointsPerColumn(
                                      selectedColumn,
                                      investorType as eInvestorType,
                                      getAssetTypeInt(assetTypeUrlParam)
                                    )}
                                  />
                                }
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {rankingItems.length === 0 && (
                        <div className="row row-sm">
                          <div className="col-md">
                            <div className="card card-minimal-four">
                              <div className="card-header">
                                <div style={{ textAlign: "center" }}>
                                  <span>
                                    <AiOutlineCloudDownload size={48} />
                                  </span>
                                  <h4>Coletando dados</h4>
                                  <span>
                                    Lucy está coletando dados para exibir o
                                    ranking com mais precisão. Por favor, volte
                                    mais tarde :)
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <nav className="mt-4">
                    <ul className="pagination justify-content-center">
                      <li
                        className={`page-item pd-10 ${actualPage === 1 ? "disabled" : ""
                          }`}
                        onClick={() => {
                          if (actualPage === 1) {
                            return;
                          }

                          return setActualPage(actualPage - 1);
                        }}
                      >
                        <a
                          className="page-link"
                          href="#?"
                          style={{ borderRadius: 50 }}
                        >
                          <i>
                            <MdNavigateBefore />
                          </i>
                        </a>
                      </li>
                      <li className="mt-3">
                        {actualPage}/{totalPages}
                      </li>
                      <li
                        className={`page-item float-right pd-10 ${!hasNextPage ? "disabled" : ""
                          }`}
                        onClick={() => {
                          if (!hasNextPage) {
                            return;
                          }

                          setActualPage(actualPage + 1);
                        }}
                      >
                        <a
                          className="page-link float-right"
                          href="#?"
                          style={{ borderRadius: 50 }}
                        >
                          <i>
                            <MdNavigateNext />
                          </i>
                        </a>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const iconSizes = {
  navButtons: 20,
  filterButtons: 15,
  barIcons: 22,
};

function getBarIcon(assetType: string): JSX.Element {
  const iconSize = iconSizes.barIcons;
  switch (assetType) {
    case "stock":
      return <MdOutlineBusiness size={iconSize} color={projectPrimaryColor} />;
    case "forex":
      return <RiStockLine size={iconSize} color={projectPrimaryColor} />;
    default:
      return <GiRocketFlight size={iconSize} color={projectPrimaryColor} />;
  }
}

function getAssetTypeInt(assetTypeText: string): number {
  switch (assetTypeText) {
    case "stock":
      return 1;
    case "crypto":
      return 3;

    default:
      return 0;
  }
}

function getMaxPointsPerColumn(
  column: string,
  investorType: eInvestorType,
  assetType: eAssetType
): number {
  const isHolder = investorType === eInvestorType.BUY_AND_HOLDER;

  const maxPointsStockDictionary: Dictionary<number> = {
    ai_score: 100,
    health_buy: isHolder ? 15 : 5,
    fundamental_buy: 8,
  };

  const maxPointsCriptoDictionary: Dictionary<number> = {
    ai_score: 100,
    technical_buy: 9,
    general_qnt_buy: 13,
  };

  if (assetType === eAssetType.STOCK) {
    return maxPointsStockDictionary[column];
  }

  return maxPointsCriptoDictionary[column];
}

const stockButtons: NavButtonitem[] = [
  {
    title: "Lucy AI - Score",
    column: "ai_score",
    color: "yellow",
    icon: <IoSparklesOutline size={iconSizes.navButtons} />,
  },
  {
    title: "Saúde financeira",
    column: "health_buy",
    color: "green",
    icon: <MdOutlineMultilineChart size={iconSizes.navButtons} />,
  },
  {
    title: "Análise fundamentalista",
    column: "fundamental_buy",
    color: "blue",
    icon: <HiPresentationChartLine size={iconSizes.navButtons} />,
  },
];

const criptoButtons: NavButtonitem[] = [
  {
    title: "Lucy AI - Score",
    column: "ai_score",
    color: "yellow",
    icon: <IoSparklesOutline size={iconSizes.navButtons} />,
  },
  {
    title: "Todas análises",
    column: "general_qnt_buy",
    color: "green",
    icon: <MdOutlineMultilineChart size={iconSizes.navButtons} />,
  },
  {
    title: "Análise técnica",
    column: "technical_buy",
    color: "blue",
    icon: <HiPresentationChartLine size={iconSizes.navButtons} />,
  },
];
